import { useEffect, useRef } from 'react';

/**
 * Hook personalizado para observar cambios de tamaño en un elemento
 * y comunicarlos al padre mediante postMessage
 * Mejora: Fuerza la medición cuando cambia el contenido
 * @param {Function} onResize - Callback opcional cuando el tamaño cambia
 * @returns {Object} ref - Referencia para adjuntar al elemento
 */
export const useResizeObserver = (onResize) => {
  const elementRef = useRef(null);
  const tickingRef = useRef(false);
  const lastHeightRef = useRef(0);
  const observerRef = useRef(null);

  // Función para medir y comunicar el tamaño
  const notifyResize = (element) => {
    if (!element) return;

    if (!tickingRef.current) {
      tickingRef.current = true;

      window.requestAnimationFrame(() => {
        if (!element) {
          tickingRef.current = false;
          return;
        }

        const height = element.getBoundingClientRect().height;
        
        // Solo notificar si la altura cambió significativamente
        if (Math.abs(height - lastHeightRef.current) > 1) {
          lastHeightRef.current = height;
          
          // Ejecutar callback si existe
          if (typeof onResize === 'function') {
            onResize(height);
          }

          // Notificar al padre (para iframes/embeds)
          window.parent.postMessage({ type: 'embed-size', height }, '*');
        }

        tickingRef.current = false;
      });
    }
  };

  useEffect(() => {
    if (!elementRef.current) return;

    const element = elementRef.current;

    // Crear ResizeObserver
    observerRef.current = new ResizeObserver(() => {
      notifyResize(element);
    });

    observerRef.current.observe(element);

    // Medir inmediatamente
    setTimeout(() => notifyResize(element), 0);

    // Limpiar
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [onResize]);

  // Usar MutationObserver para detectar cambios de contenido (cuando cambian tabs)
  useEffect(() => {
    if (!elementRef.current) return;

    const element = elementRef.current;

    // MutationObserver para detectar cuando cambia el contenido
    const mutationObserver = new MutationObserver(() => {
      // Esperar a que el contenido se renderice completamente
      requestAnimationFrame(() => {
        notifyResize(element);
      });
    });

    mutationObserver.observe(element, {
      childList: true,        // Detectar cambios en hijos
      subtree: true,          // Detectar cambios en todos los descendientes
      attributes: true,       // Detectar cambios de atributos
      attributeFilter: ['class', 'style'], // Filtrar solo ciertos atributos
      characterData: false,   // No necesitamos monitorear cambios de texto
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, []);

  return elementRef;
};
